import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession, signOut } from '@/lib/auth-client';
import { cn } from '@/components/ui/cn';
import { LodestarLogo } from '@/components/brand';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ShareButton } from './ShareButton';
import { ShortlistButton } from './ShortlistButton';
import { BulkImportButton } from './BulkImportButton';
import { HeaderHelpMenu } from '@/features/guide';
import { countriesQuery } from '@/data';
import { buildWikiUrl } from '@/features/wiki/architecture';
import { PUBLISHED_COUNTRY_CODES } from '@/features/wiki/published-countries';
import { checklistPath } from '@/features/next-steps/slugs';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { localizePath } from '@/i18n/paths';
import { uiLang } from '@/i18n/lang';

export interface MarketingHeaderProps {
  /** When true, header gets a faint border + blur once scrolled > 40px. */
  blurOnScroll?: boolean;
  /** Render the contextual help menu (map app only). */
  withHelpMenu?: boolean;
  /** Render Bulk Import + Shortlist buttons (map app only). */
  withShortlist?: boolean;
  /** Render Share button (map app only). */
  withShare?: boolean;
}

export function MarketingHeader({
  blurOnScroll = true,
  withHelpMenu = false,
  withShortlist = false,
  withShare = false,
}: MarketingHeaderProps) {
  const { t, i18n } = useTranslation();
  const lp = useLocalizedPath();
  const { data: session, isPending } = useSession();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!blurOnScroll) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [blurOnScroll]);

  const lang: 'ru' | 'en' = uiLang(i18n.language);

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex items-center justify-between px-6 py-3 transition-colors duration-200 sm:px-10',
        scrolled
          ? 'border-b border-ink-100 bg-paper/90 backdrop-blur'
          : 'border-b border-transparent bg-paper',
      )}
    >
      <Link
        to="/"
        aria-label="Lodestar — home"
        className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600"
      >
        <LodestarLogo size={30} />
      </Link>

      <div className="flex items-center gap-4">
        <CountryGuidesMenu lang={lang} />

        <nav
          aria-label={t('header.nav', { defaultValue: 'Primary' })}
          className="hidden items-center gap-5 sm:flex"
        >
          <Link
            to={lp('/map')}
            className="font-mono text-xs uppercase tracking-wider text-ink-500 transition-colors hover:text-ink-900"
          >
            {t('home.header.map')}
          </Link>
          <Link
            to={lp('/countries')}
            className="font-mono text-xs uppercase tracking-wider text-ink-500 transition-colors hover:text-ink-900"
          >
            {t('home.header.atlas')}
          </Link>
          <Link
            to={lp('/guide')}
            className="font-mono text-xs uppercase tracking-wider text-ink-500 transition-colors hover:text-ink-900"
          >
            {t('home.header.howTo')}
          </Link>
        </nav>

        <LanguageSwitcher />
        {withHelpMenu && <HeaderHelpMenu />}
        {withShortlist && <BulkImportButton />}
        {withShortlist && <ShortlistButton />}
        {withShare && <ShareButton />}

        {isPending ? null : session ? (
          <button
            onClick={() => signOut()}
            className="rounded-md border border-ink-100 px-2.5 py-1 text-sm text-ink-700 hover:bg-paper-2"
          >
            {t('header.signOut')}
          </button>
        ) : (
          <Link
            to="/login"
            className="rounded-md bg-terra-600 px-3 py-1.5 text-sm font-medium text-paper hover:bg-terra-700"
          >
            {t('header.signIn')}
          </Link>
        )}
      </div>
    </header>
  );
}

/**
 * Hover/click dropdown surfacing the 4 fully-published country guides
 * plus a link to the full atlas. Closes on outside-click or Esc.
 * Hidden on the smallest screens — the SiteFooter carries the same links
 * for mobile users.
 */
function CountryGuidesMenu({ lang }: { lang: 'ru' | 'en' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { data: file } = useQuery(countriesQuery());

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const names = useMemo(() => {
    const map: Record<string, string> = {};
    file?.countries.forEach((c) => {
      map[c.code] = lang === 'ru' ? c.name_ru : c.name_en;
    });
    return map;
  }, [file, lang]);

  return (
    <div className="relative hidden sm:block" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-ink-500 transition-colors hover:text-ink-900"
        title={t('header.guidesTooltip', { defaultValue: 'Country encyclopedias' })}
      >
        {t('header.countryGuides', { defaultValue: 'Country guides' })}
        <span aria-hidden="true" className="text-[10px]">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[1100] mt-2 w-64 rounded-lg border border-ink-100 bg-paper p-2 shadow-lg"
        >
          <p className="px-3 pb-2 pt-1 font-mono text-[10px] uppercase tracking-wider text-ink-500">
            {t('header.guidesTitle', { defaultValue: 'Country guides' })}
          </p>
          <ul className="space-y-0.5">
            {PUBLISHED_COUNTRY_CODES.map((cc) => {
              const country = file?.countries.find((c) => c.code === cc);
              return (
                <li key={cc} role="none">
                  <Link
                    role="menuitem"
                    to={buildWikiUrl({ lang, cc })}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-1.5 text-sm text-ink-700 hover:bg-paper-2 hover:text-ink-900"
                  >
                    <span aria-hidden="true" className="text-base leading-none">
                      {country?.flag_emoji ?? ''}
                    </span>
                    <span className="flex-1">{names[cc] ?? cc}</span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-terra-600">
                      {t('header.guidesFull', { defaultValue: 'Guide' })}
                    </span>
                  </Link>
                  <Link
                    role="menuitem"
                    to={checklistPath(cc)}
                    onClick={() => setOpen(false)}
                    className="ml-9 mb-1 flex items-center justify-between rounded-md px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-500 hover:bg-paper-2 hover:text-ink-900"
                  >
                    <span>↳ {t('header.guidesChecklist', { defaultValue: 'Moving checklist' })}</span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="mt-2 border-t border-ink-100 pt-2">
            <Link
              role="menuitem"
              to={localizePath('/countries', lang)}
              onClick={() => setOpen(false)}
              className="block rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:bg-paper-2 hover:text-ink-900"
            >
              {t('header.guidesAll', { defaultValue: 'All 31 countries →' })}
            </Link>
            <Link
              role="menuitem"
              to={lang === 'ru' ? '/ru/topics' : '/topics'}
              onClick={() => setOpen(false)}
              className="block rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:bg-paper-2 hover:text-ink-900"
            >
              {t('header.guidesTopics', { defaultValue: 'Browse by topic →' })}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
